import React, { useState, useEffect } from 'react';
import { Modal } from '~/components/ui/Modal';
import { ArrowLeft, X, Settings } from 'lucide-react';
import PresetCard from './PresetCard';
import { Dropdown, type DropdownOption } from '~/components/ui/Dropdown';
import { useTranslation } from '~/lib/hooks/useTranslation';
import {
  type Framework,
  type Language,
  FRAMEWORK_CONFIGS,
  setFramework,
  setLanguage,
  isLanguageKey,
  techStackSelectionStore,
} from '~/lib/stores/techStackSelection';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { useStore } from '@nanostores/react';
import { tabViewEnabled } from '~/utils/constants';
import { userStore } from '~/lib/stores/user';
import { FrameworkIcons } from '~/lib/ui/icons';

interface PresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: (selectedPreset: string) => void;
  onUpgrade?: () => void;
}

const frameworkOptions: DropdownOption[] = Object.entries(FRAMEWORK_CONFIGS).map(([key, config]) => ({
  value: key,
  label: config.label,
  icon: config.icon,
}));

export default function PresetModal({ isOpen, onClose, onContinue, onUpgrade }: PresetModalProps) {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState<string>('clean-canvas');
  const persistedSelection = useStore(selectionStore);
  const userData = useStore(userStore);
  const isFreePlan = tabViewEnabled && !userData.isSubscribed;

  const presets = [
    {
      id: 'single-page',
      icons: ['html', 'tailwind'],
      title: t('presetModal.presets.singlePage.title'),
      description: t('presetModal.presets.singlePage.description'),
      framework: 'html_tailwind' as Framework,
      language: 'js' as Language,
    },
    {
      id: 'modern-web-app',
      icons: ['react', 'shadcn'],
      title: t('presetModal.presets.modernWebApp.title'),
      description: t('presetModal.presets.modernWebApp.description'),
      framework: 'react_shadcn' as Framework,
      language: 'ts' as Language,
    },
    {
      id: 'mobile-apps',
      icons: ['android', 'apple'],
      title: t('presetModal.presets.mobileApps.title'),
      description: t('presetModal.presets.mobileApps.description'),
      framework: 'reactnative' as Framework,
      language: 'ts' as Language,
    },
    {
      id: 'clean-canvas',
      icons: ['react', 'tailwind'],
      title: t('presetModal.presets.default.title'),
      description: t('presetModal.presets.default.description'),
      framework: 'react_tailwind' as Framework,
      language: 'ts' as Language,
    },
  ];

  const [view, setView] = useState<'presets' | 'custom'>(
    tabViewEnabled ||
      (persistedSelection.hasInteractedWithModal === 'true' &&
        persistedSelection.rememberCustomStack === 'true' &&
        persistedSelection.framework)
      ? 'custom'
      : 'presets',
  );
  const [customFramework, setCustomFramework] = useState<Framework>(persistedSelection.framework);
  const [customLanguage, setCustomLanguage] = useState<Language>(persistedSelection.language);
  const [rememberChoice, setRememberChoice] = useState<boolean>(persistedSelection.rememberCustomStack === 'true');

  const selectedFramework = frameworkOptions.find((f) => f.value === customFramework);

  useEffect(() => {
    if (isOpen && persistedSelection.hasInteractedWithModal === 'false') {
      selectionStore.setKey('hasInteractedWithModal', 'true');
    }
  }, [isOpen, persistedSelection.hasInteractedWithModal]);

  useEffect(() => {
    const config = FRAMEWORK_CONFIGS[customFramework];

    if (config && !config.languages.includes(customLanguage)) {
      setCustomLanguage(config.defaultLanguage);
    }
  }, [customFramework, customLanguage]);

  useEffect(() => {
    selectionStore.setKey('rememberCustomStack', rememberChoice ? 'true' : 'false');

    if (rememberChoice) {
      selectionStore.setKey('framework', customFramework);
      selectionStore.setKey('language', customLanguage);
      selectionStore.setKey('presetModalView', view);
    } else {
      selectionStore.setKey('framework', 'react_tailwind');
      selectionStore.setKey('language', 'ts');
    }
  }, [customFramework, customLanguage, view, rememberChoice]);

  const handleContinue = () => {
    if (view === 'presets') {
      const preset = presets.find((p) => p.id === selectedPreset);

      if (preset) {
        setFramework(preset.framework);
        setLanguage(preset.language);
        selectionStore.setKey('framework', preset.framework);
        selectionStore.setKey('language', preset.language);
      }
    } else {
      setFramework(customFramework);
      setLanguage(customLanguage);

      if (rememberChoice) {
        selectionStore.setKey('framework', customFramework);
        selectionStore.setKey('language', customLanguage);
      }
    }

    selectionStore.setKey('presetModalView', view);
    onContinue?.(selectedPreset);
    onClose();
  };

  const handleLetMeChoose = () => {
    setView('custom');
  };

  const handleBackToPresets = () => {
    setView('presets');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-dualite-background-tertiary w-full max-w-[600px] rounded-xl border border-dualite-border-primary p-5 sm:p-6 flex flex-col gap-4 text-dualite-text-primary">
        <header className="flex justify-between items-center">
          {tabViewEnabled ? (
            <div className="w-6" />
          ) : (
            <button
              onClick={view === 'custom' ? handleBackToPresets : onClose}
              className="text-dualite-text-secondary hover:text-dualite-text-primary transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <h2 className="font-inter font-medium text-sm">
            {view === 'presets' ? t('presetModal.headerPresets') : t('presetModal.headerCustom')}
          </h2>
          <button
            onClick={onClose}
            className="text-dualite-text-secondary hover:text-dualite-text-primary transition-colors"
          >
            <X size={24} />
          </button>
        </header>

        {view === 'presets' ? (
          <>
            <p className="text-center text-dualite-text-secondary text-xs font-medium px-4">
              {t('presetModal.descriptionPresets')}
            </p>

            <main className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {presets.map((preset) => (
                <PresetCard
                  key={preset.id}
                  {...preset}
                  isSelected={selectedPreset === preset.id}
                  onClick={() => setSelectedPreset(preset.id)}
                />
              ))}
            </main>

            <footer className="flex flex-col-reverse sm:flex-row justify-between items-center gap-4 pt-2">
              <button
                onClick={handleLetMeChoose}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-dualite-background-button hover:bg-dualite-background-buttonHover border border-dualite-border-primary rounded-lg px-3 py-2 text-xs font-medium transition-colors"
              >
                <Settings size={16} />
                {t('presetModal.customize')}
              </button>
              <button
                onClick={handleContinue}
                className="w-full sm:w-auto bg-dualite-background-cta hover:bg-dualite-background-ctaHover text-dualite-text-cta rounded-lg px-6 py-2.5 text-xs font-medium shadow-sm transition-all"
              >
                {t('presetModal.continue')}
              </button>
            </footer>
          </>
        ) : (
          <>
            <p className="text-center text-dualite-text-secondary text-xs font-medium px-4">
              {t('presetModal.descriptionCustom')}
            </p>

            <main className="flex flex-col gap-4 px-4">
              <Dropdown
                id="custom-framework-select"
                options={frameworkOptions}
                value={customFramework}
                onChange={(value) => setCustomFramework(value as Framework)}
                label={t('presetModal.framework')}
                className="w-full"
              />

              <Dropdown
                id="custom-language-select"
                options={[
                  {
                    value: 'ts',
                    label: 'TypeScript',
                    icon: FrameworkIcons.typescript,
                    disabled: !FRAMEWORK_CONFIGS[customFramework]?.languages.includes('ts' as Language),
                  },
                  {
                    value: 'js',
                    label: 'JavaScript',
                    icon: FrameworkIcons.javascript,
                    disabled: !FRAMEWORK_CONFIGS[customFramework]?.languages.includes('js' as Language),
                  },
                ]}
                value={customLanguage}
                onChange={(value) => {
                  if (isLanguageKey(value)) {
                    setCustomLanguage(value);
                  }
                }}
                label={t('presetModal.language')}
                className="w-full"
              />
            </main>

            <footer className="flex justify-between items-center gap-3 pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-choice"
                  checked={rememberChoice}
                  onChange={(e) => setRememberChoice(e.target.checked)}
                  className="h-4 w-4 rounded border-dualite-border-primary text-dualite-primary-start focus:ring-dualite-primary-start"
                />
                <label htmlFor="remember-choice" className="text-xs text-dualite-text-secondary">
                  {t('presetModal.rememberChoice')}
                </label>
              </div>
              {isFreePlan ? (
                <button
                  onClick={() => onUpgrade?.()}
                  className="flex items-center gap-1.5 rounded-lg px-6 py-2.5 text-xs font-semibold shadow-sm transition-all"
                  style={{
                    background: '#233B22',
                    border: '1px solid #8DD29066',
                    color: '#8dd290',
                  }}
                >
                  <div className="i-ph:lightning-fill text-xs" />
                  <span>Upgrade to Continue</span>
                </button>
              ) : (
                <button
                  onClick={handleContinue}
                  className="bg-dualite-background-cta hover:bg-dualite-background-ctaHover text-dualite-text-cta rounded-lg px-6 py-2.5 text-xs font-medium shadow-sm transition-all"
                >
                  {t('presetModal.continue')}
                </button>
              )}
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
}
